import { useDebounce } from '@/hooks/useDebounce';
import { useUrlState } from '@/hooks/useUrlState';
import {
  Box,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { styled } from '@mui/material/styles';
import { Select } from '@liminal/web-ui/forms/Select';
import { useTransactions } from '@/hooks/useTransactions';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { formatOrderDate } from '@liminal/utils/date';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { txContextVariant, txStatusVariant, formatEnumLabel } from '@/components/ui/statusHelpers';
import { AccountIdLink } from '@/components/ui/AccountIdLink';
import { TransactionIdLink } from '@/components/ui/TransactionIdLink';
import { MonoCell } from '@/components/ui/MonoCell';
import { SearchInput } from '@liminal/web-ui/forms/SearchInput';
import { LedgerDirection } from '@/api/generated';
import type { Transaction, TransactionEntry } from '@/api/types';

const PAGE_SIZE = 50;

const CONTEXT_OPTIONS = [
  { label: 'All Contexts', value: '' },
  { label: 'P2P', value: 'TRANSACTION_CONTEXT_P2P' },
  { label: 'Onramp', value: 'TRANSACTION_CONTEXT_ONRAMP' },
  { label: 'Offramp', value: 'TRANSACTION_CONTEXT_OFFRAMP' },
  { label: 'Fiat to Fiat', value: 'TRANSACTION_CONTEXT_FIAT_TO_FIAT' },
  { label: 'Teleport', value: 'TRANSACTION_CONTEXT_TELEPORT' },
  { label: 'Savings Deposit', value: 'TRANSACTION_CONTEXT_SAVINGS_DEPOSIT' },
  { label: 'Savings Withdraw', value: 'TRANSACTION_CONTEXT_SAVINGS_WITHDRAW' },
  { label: 'Stock Buy', value: 'TRANSACTION_CONTEXT_STOCK_BUY' },
  { label: 'Stock Sell', value: 'TRANSACTION_CONTEXT_STOCK_SELL' },
  { label: 'Pix Payment', value: 'TRANSACTION_CONTEXT_PIX_PAYMENT' },
  { label: 'Pix Received', value: 'TRANSACTION_CONTEXT_PIX_RECEIVE' },
  { label: 'Boleto Payment', value: 'TRANSACTION_CONTEXT_PIX_BOLETO' },
  { label: 'Paid with LIL', value: 'TRANSACTION_CONTEXT_REWARD_REDEMPTION' },
];

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: '' },
  { label: 'Pending', value: 'TRANSACTION_STATUS_PENDING' },
  { label: 'Confirmed', value: 'TRANSACTION_STATUS_CONFIRMED' },
  { label: 'Failed', value: 'TRANSACTION_STATUS_FAILED' },
];

const entriesByDirection = (tx: Transaction, direction: LedgerDirection): TransactionEntry[] =>
  (tx.entries ?? []).filter((entry) => entry.direction === direction);

/**
 * Truncates a ledger decimal string to 2dp for display, as text — the shared
 * formatAmount goes through parseFloat and rewrites USDC as $, and an admin
 * ledger view wants the raw asset and no float anywhere near the amount.
 * `exact` is false only when nonzero digits were dropped; the cell then shows
 * the full value in a tooltip.
 */
const formatEntryAmount = (amount: string): { display: string; exact: boolean } => {
  const point = amount.indexOf('.');
  const whole = point === -1 ? amount : amount.slice(0, point);
  const fraction = point === -1 ? '' : amount.slice(point + 1);
  return {
    display: `${whole}.${fraction.slice(0, 2).padEnd(2, '0')}`,
    exact: !/[^0]/.test(fraction.slice(2)),
  };
};

/**
 * One entry per sortable header. The ledger returns a page ordered by recency
 * and takes no sort parameter, so this reorders the page on screen and not the
 * whole filtered set — narrow with the filters above first, then sort.
 *
 * Amounts are handed over as the raw decimal string: the comparator orders
 * decimals exactly, and converting here would put money through a float. The
 * debit/credit cells can stack several entries (fees), so each sorts by its
 * first entry's amount.
 */
const SORT_ACCESSORS = {
  id: (tx: Transaction) => tx.id,
  context: (tx: Transaction) => formatEnumLabel(tx.context),
  status: (tx: Transaction) => formatEnumLabel(tx.status),
  debit: (tx: Transaction) => entriesByDirection(tx, LedgerDirection.DIRECTION_DEBIT)[0]?.amount,
  credit: (tx: Transaction) =>
    entriesByDirection(tx, LedgerDirection.DIRECTION_CREDIT)[0]?.amount,
  accountId: (tx: Transaction) => tx.entries?.[0]?.accountId,
  createdAt: (tx: Transaction) => tx.createdAt,
};

const COLUMN_COUNT = 7;

// -- Styled components --

// One line per entry: a side usually holds one entry, but fees add more.
const EntryLines = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

// One side (debit or credit) of a transaction row: 2dp per entry, with the
// exact ledger value in a tooltip whenever the display dropped digits.
function EntrySideCell({ entries }: { entries: TransactionEntry[] }) {
  if (entries.length === 0) return <TableCell>{'—'}</TableCell>;
  return (
    <TableCell>
      <EntryLines>
        {entries.map((entry, entryIndex) => {
          const key = entry.id ?? entryIndex;
          if (!entry.amount) return <span key={key}>{'—'}</span>;
          const { display, exact } = formatEntryAmount(entry.amount);
          if (exact) {
            return (
              <span key={key}>
                {display} {entry.assetSymbol}
              </span>
            );
          }
          return (
            <Tooltip key={key} title={`${entry.amount} ${entry.assetSymbol}`} placement="top">
              <span>
                {display} {entry.assetSymbol}
              </span>
            </Tooltip>
          );
        })}
      </EntryLines>
    </TableCell>
  );
}

/** The filters as they appear in the URL. `user` is the account id the search box sets. */
const FILTER_DEFAULTS = { user: '', context: '', status: '', asset: '' };

// -- Component --

export function TransactionsPage() {
  const [params, setParams] = useUrlState(FILTER_DEFAULTS);
  const { user: search, context, status, asset: assetSymbol } = params;

  const debouncedSearch = useDebounce(search, 300);
  const debouncedAsset = useDebounce(assetSymbol, 300);

  const pagination = useCursorPagination(PAGE_SIZE);
  const { reset: resetPaging } = pagination;

  // Every filter here is applied by the server, so a page of results only means
  // anything alongside the filters that produced it — changing one has to send
  // us back to page 1 rather than reuse a cursor into the previous result set.
  const changeFilter = (patch: Partial<typeof FILTER_DEFAULTS>) => {
    setParams(patch);
    resetPaging();
  };

  const filters = {
    ...(debouncedSearch ? { accountId: debouncedSearch } : {}),
    ...(context ? { context } : {}),
    ...(status ? { status } : {}),
    // The ledger compares asset_symbol with `=`, not ILIKE, so a typed "usdc"
    // matches nothing at all rather than the user's USDC history.
    ...(debouncedAsset ? { assetSymbol: debouncedAsset.trim().toUpperCase() } : {}),
    pageSize: PAGE_SIZE,
    pageToken: pagination.pageToken,
  };

  const { data, isLoading, isError } = useTransactions(filters);
  const nextPageToken = data?.nextPageToken ?? '';

  // Seeded on Created At descending because that is the order the ledger
  // already returns, so the header states the truth before anything is clicked.
  const { rows: transactions, sort } = useTableSort(
    data?.transactions ?? [],
    SORT_ACCESSORS,
    'createdAt',
    'desc',
  );

  return (
    <Stack spacing={3}>
      {/* Filters */}
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
        {/* Deliberately not "or transaction ID": this box sets account_id, which
            the ledger matches exactly against an account, so a transaction id
            typed here has always returned nothing. Looking one up by id needs
            GET /admin/v1/transactions/{id}, which this page does not call. */}
        <SearchInput
          value={search}
          onChange={(value) => changeFilter({ user: value })}
          placeholder="Filter by user ID"
          width={340}
        />
        <Select
          variant="filter"
          aria-label="Context"
          size="sm"
          width={180}
          value={context}
          onChange={(value) => changeFilter({ context: value })}
          options={CONTEXT_OPTIONS}
        />
        <Select
          variant="filter"
          aria-label="Status"
          size="sm"
          width={160}
          value={status}
          onChange={(value) => changeFilter({ status: value })}
          options={STATUS_OPTIONS}
        />
        <SearchInput
          value={assetSymbol}
          onChange={(value) => changeFilter({ asset: value })}
          placeholder="Asset (e.g. USDC)"
          width={200}
        />
      </Stack>

      {/* Data Table */}
      <Card variant="elevated" padding="none">
        <Table>
          <TableHead>
            <TableRow>
              <SortableTableCell column="id" sort={sort}>
                ID
              </SortableTableCell>
              <SortableTableCell column="context" sort={sort}>
                Context
              </SortableTableCell>
              <SortableTableCell column="status" sort={sort}>
                Status
              </SortableTableCell>
              <SortableTableCell column="debit" sort={sort}>
                Debit
              </SortableTableCell>
              <SortableTableCell column="credit" sort={sort}>
                Credit
              </SortableTableCell>
              <SortableTableCell column="accountId" sort={sort}>
                Account
              </SortableTableCell>
              <SortableTableCell column="createdAt" sort={sort}>
                Created At
              </SortableTableCell>
            </TableRow>
          </TableHead>
          <DataTableBody
            columns={COLUMN_COUNT}
            isLoading={isLoading}
            isError={isError}
            isEmpty={transactions.length === 0}
            loadingRows={8}
            emptyMessage="No transactions found. Try adjusting your filters."
            errorMessage="Could not load transactions."
          >
            {transactions.map((tx) => {
              const debits = entriesByDirection(tx, LedgerDirection.DIRECTION_DEBIT);
              const credits = entriesByDirection(tx, LedgerDirection.DIRECTION_CREDIT);
              return (
                <TableRow key={tx.id} hover>
                  <MonoCell>
                    <TransactionIdLink transactionId={tx.id} />
                  </MonoCell>
                  <TableCell>
                    <StatusPill
                      label={formatEnumLabel(tx.context)}
                      tone={txContextVariant(tx.context)}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell>
                    <StatusPill
                      label={formatEnumLabel(tx.status)}
                      tone={txStatusVariant(tx.status)}
                      size="sm"
                    />
                  </TableCell>
                  <EntrySideCell entries={debits} />
                  <EntrySideCell entries={credits} />
                  <TableCell>
                    <AccountIdLink accountId={tx.entries?.[0]?.accountId} />
                  </TableCell>
                  <TableCell>{tx.createdAt ? formatOrderDate(tx.createdAt) : '\u2014'}</TableCell>
                </TableRow>
              );
            })}
          </DataTableBody>
        </Table>
      </Card>

      {/* No "of N": the ledger returns a cursor and no total, and counting the
          whole filtered set to produce one is a query this page does not get
          to make. */}
      <CursorPagination
        firstRowNumber={pagination.firstRowNumber}
        count={transactions.length}
        hasPreviousPage={pagination.hasPreviousPage}
        hasNextPage={!!nextPageToken}
        onPreviousPage={pagination.goToPreviousPage}
        onNextPage={() => pagination.goToNextPage(nextPageToken)}
        disabled={isLoading}
      />
    </Stack>
  );
}
